import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle, ArrowLeft } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import TaskModal from "../../pages/Lead/AddTask";
import EditTaskModal from "../../pages/Lead/EditTask";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/TaskLead/TaskTable";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

// Define API response structure
interface APITask {
  task_id: number;
  task_name: string;
  lead_name: string;
  task_description: string;
  status: string;
  start_datetime: string;
  due_date: string;
  duration: number;
}

// Define Task structure for frontend
interface Task {
  task_id: number;
  task_name: string;
  lead_name: string;
  task_description: string;
  status: string;
  start_datetime: string;
  due_date: string;
  duration: number;
}

const TaskTable: React.FC = () => {
  const { lead_id } = useParams<{ lead_id: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Task | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [leadName, setLeadName] = useState<string>(""); // Added leadName state

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  const handleDeleteClick = (taskId: number) => {
    setSelectedTaskId(taskId);
    openModal();
  };

  const handleEditClick = (taskId: number) => {
    setSelectedTaskId(taskId);
    setShowEditTaskModal(true);
  };

  const handleTaskAdded = () => {
    fetchTasks(); // Refetch tasks to update the table
  };

  const handleTaskUpdated = (updatedTask: Task | undefined) => {
    if (!updatedTask || !updatedTask.task_id) {
      console.error("Invalid updatedTask:", updatedTask);
      fetchTasks(); // Fallback to refetching tasks if update fails
      return;
    }
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.task_id === updatedTask.task_id ? updatedTask : task
      )
    );
  };

  const handleConfirmDelete = async () => {
    if (!selectedTaskId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/task/delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ task_id: selectedTaskId }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 200) {
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        setTasks((prevTasks) => {
          const updatedTasks = prevTasks.filter(
            (task) => task.task_id !== selectedTaskId
          );
          if (updatedTasks.length > 0 && displayedTasks.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedTasks;
        });
        closeModal();
      } else {
        toast.error(result.message || "Failed to delete task.");
      }
    } catch (error) {
      console.error("Network error:", error);
      toast.error("An unexpected error occurred while deleting the task.");
    }
  };

  // Memoize fetchTasks to prevent unnecessary re-renders
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/task/search/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            lead_id: lead_id || "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 200) {
        const formattedTasks: Task[] = Array.isArray(result.data)
          ? result.data.map((task: APITask) => ({
            task_id: task.task_id,
            task_name: task.task_name,
            task_description: task.task_description,
            lead_name: task.lead_name,
            status: task.status,
            start_datetime: task.start_datetime,
            due_date: task.due_date,
            duration: task.duration,
          }))
          : [];
        setTasks(formattedTasks);
        setLeadName(formattedTasks.length > 0 ? formattedTasks[0].lead_name : "Unknown Lead");
      } else {
        setTasks([]);
        setLeadName("Unknown Lead");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setTasks([]);
      setLeadName("Unknown Lead");
    } finally {
      setLoading(false);
    }
  }, [lead_id]);

  // Filter and sort Tasks
  const filteredTasks = tasks.filter((task) =>
    Object.values(task)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? (valA as number) - (valB as number)
        : (valB as number) - (valA as number);
  });

  const displayedTasks = sortedTasks.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredTasks.length / rowsPerPage);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Task) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredTasks
      : sortColumn
        ? sortedTasks
        : tasks;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((task) => ({
      "Task Name": task.task_name,
      "Start Name": task.start_datetime,
      "Due Date": task.due_date,
      "Status": task.status,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [[`Task Data List for ${leadName}`]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          `Task Data List for ${leadName}`.length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tasks");
    XLSX.writeFile(
      workbook,
      searchTerm ? `TaskData_${searchTerm}.xlsx` : `TaskData_${leadName}.xlsx`
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Task List For {leadName}</h3>
          <Link
            to="/lead-list"
            className={styles.back}
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.headerControls}>
            <RowsPerPageSelector
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
            <div className={styles.controlsInner}>
              <div className={styles.searchContainer}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className={styles.downloadButton}
                onClick={handleDownload}
              >
                Download
                <DownloadIcon className={styles.downloadIcon} />
              </button>
              <button
                onClick={() => setShowTaskModal(true)}
                className={styles.addButton}
              >
                Add New Task
                <PlusIcon />
              </button>
              <TaskModal
                state={{ lead_id: lead_id }}
                isOpen={showTaskModal}
                onClose={() => setShowTaskModal(false)}
                onTaskAdded={handleTaskAdded}
              />
            </div>
          </div>
          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.theadRow}>
                  <th
                    className={styles.thName}
                    onClick={() => handleSort("task_name")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Task Name</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("start_datetime")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Start Date</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("due_date")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Due Date</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th
                    className={styles.thRole}
                    onClick={() => handleSort("status")}
                  >
                    <div className={styles.thSortable}>
                      <p className={styles.thText}>Status</p>
                      <button className={styles.sortButton}>
                        <DownArrow />
                        <UpArrow />
                      </button>
                    </div>
                  </th>
                  <th className={styles.thAction}>
                    <p className={styles.thText}>Action</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={8} className="text-center py-4">
                      <Loader />
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={5} className={styles.noDataText}>
                      Your task list is currently empty. Use the ‘Add New Task’ button to create one.
                    </td>
                  </tr>
                ) : filteredTasks.length === 0 ? (
                  <tr className={styles.loadingRow}>
                    <td colSpan={5} className={styles.noDataText}>
                      No records match your search.
                    </td>
                  </tr>
                ) : (
                  displayedTasks.map((task) => (
                    <tr key={task.task_id} className={styles.tbodyRow}>
                      <td className={styles.tdName}>{task.task_name}</td>
                      <td className={styles.tdRole}>{task.start_datetime}</td>
                      <td className={styles.tdRole}>{task.due_date}</td>
                      <td className={styles.tdRole}>{task.status}</td>
                      <td className={styles.tdAction}>
                        <div className={styles.actionContainer}>
                          <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                            <button
                              onClick={() => handleEditClick(task.task_id)}
                              className={styles.editButton}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>

                          {selectedTaskId === task.task_id && (
                            <EditTaskModal
                              state={{
                                lead_id: lead_id || "",
                                task_id: selectedTaskId?.toString() || "",
                              }}
                              isOpen={showEditTaskModal}
                              onClose={() => setShowEditTaskModal(false)}
                              onTaskUpdated={handleTaskUpdated}
                            />
                          )}

                          <Tooltip content="Delete" bgColor="bg-red-600">
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDeleteClick(task.task_id)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            filteredLength={filteredTasks.length}
            handlePageChange={handlePageChange}
          />
        </div>

        <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
          <button
            onClick={closeModal}
            className={styles.modalCloseButton}
          ></button>
          <div className="flex justify-center">
            <div className={styles.modalIconContainer}>
              <FontAwesomeIcon icon={faTrash} className={styles.modalIcon} />
            </div>
          </div>
          <h4 className={styles.modalTitle}>Confirm Delete</h4>
          <p className={styles.modalText}>
            Are you sure you want to delete this task?
          </p>
          <div className={styles.modalButtonContainer}>
            <button onClick={closeModal} className={styles.modalCancelButton}>
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={isDeleteSuccessModalOpen}
          onClose={() => setDeleteSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
          <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
            Task Deleted Successfully!
          </h4>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setDeleteSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default TaskTable;
